import React, { useEffect, useRef, useState } from 'react';
import { MapPin, AlertTriangle, CheckCircle2, Loader2, X } from 'lucide-react';
import { cn } from "@/lib/utils";
import { Input } from '@/components/ui/input';

interface GoogleMapsWindow extends Window {
  google?: {
    maps: {
      places: {
        Autocomplete: new (
          input: HTMLInputElement,
          options?: any
        ) => google.maps.places.Autocomplete;
      };
    };
  };
  initGoogleMaps?: () => void;
}

interface AddressAutocompleteProps {
  onAddressSelect: (address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  }) => void;
  className?: string;
}

const googleWindow = window as GoogleMapsWindow;

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  onAddressSelect,
  className = '',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      if (googleWindow.google?.maps) {
        setIsLoaded(true);
        setApiError(null);
        return;
      }

      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        if (!googleWindow.google?.maps) {
          setApiError('Google Maps failed to load. Please check your internet connection.');
        }
        return;
      }

      googleWindow.initGoogleMaps = () => {
        setIsLoaded(true);
        setApiError(null);
      };

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBbOG8oHTiYFcceToBGKAAOmlFqF3m5xQQ&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        setApiError('Failed to load Google Maps. Please try again later.');
      };
      document.head.appendChild(script);
    };

    loadGoogleMapsScript();

    return () => {
      if (googleWindow.initGoogleMaps) {
        delete googleWindow.initGoogleMaps;
      }
    };
  }, []);

  useEffect(() => {
    if (!isLoaded || !inputRef.current || !googleWindow.google?.maps?.places) {
      return;
    }

    // Clean up previous instance
    if (autocompleteRef.current) {
      google.maps.event.clearInstanceListeners(autocompleteRef.current);
    }

    try {
      const autocomplete = new googleWindow.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address'],
        types: ['address']
      });

      autocompleteRef.current = autocomplete;

      // Add place_changed listener
      google.maps.event.addListener(autocomplete, 'place_changed', () => {
        setLoading(true);
        setError(null);
        
        const place = autocomplete.getPlace();
        
        if (!place?.address_components) {
          setError('Please select a valid address from the suggestions');
          setLoading(false);
          return;
        }

        let streetNumber = '';
        let route = '';
        let city = '';
        let state = '';
        let zipCode = '';

        for (const component of place.address_components) {
          const type = component.types[0];
          switch (type) {
            case 'street_number':
              streetNumber = component.long_name;
              break;
            case 'route':
              route = component.long_name;
              break;
            case 'locality':
              city = component.long_name;
              break;
            case 'administrative_area_level_1':
              state = component.short_name;
              break;
            case 'postal_code':
              zipCode = component.long_name;
              break;
          }
        }

        if (!streetNumber || !route || !city || !state || !zipCode) {
          setError('Please select a complete address with street, city, state, and ZIP code');
          setLoading(false);
          return;
        }

        const street = `${streetNumber} ${route}`.trim();
        setInputValue(place.formatted_address || '');
        setSelectedAddress(place.formatted_address || '');
        
        onAddressSelect({
          street,
          city,
          state,
          zipCode,
        });

        setLoading(false);
      });

    } catch (err) {
      console.error('Error initializing Google Maps Autocomplete:', err);
      setApiError('Failed to initialize address autocomplete. Please try again later.');
    }

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isLoaded, onAddressSelect]);

  return (
    <div className="relative space-y-2">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setSelectedAddress(null);
            setError(null);
          }}
          placeholder={apiError ? 'Address lookup unavailable' : 'Enter address...'}
          className={cn(
            "pl-10 pr-8",
            className,
            error || apiError ? 'border-destructive focus:ring-destructive' : '',
            selectedAddress ? 'pr-10 border-green-500 focus:ring-green-500' : ''
          )}
          autoComplete="off"
          disabled={!!apiError}
          aria-label="Address autocomplete"
          aria-describedby={error || apiError ? "address-error" : undefined}
        />
        <MapPin className="absolute left-3 top-2.5 w-5 h-5 text-muted-foreground" />
        
        {loading && (
          <Loader2 className="absolute right-3 top-2.5 w-5 h-5 text-primary animate-spin" />
        )}
        
        {selectedAddress && !loading && (
          <CheckCircle2 className="absolute right-3 top-2.5 w-5 h-5 text-green-500" />
        )}

        {inputValue && !selectedAddress && !loading && (
          <button
            onClick={() => {
              setInputValue('');
              setSelectedAddress(null);
              setError(null);
              inputRef.current?.focus();
            }}
            className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {(error || apiError) && (
        <div id="address-error" role="alert" className="flex items-start space-x-2 text-destructive bg-destructive/10 p-3 rounded-md">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Address Error</p>
            <p>{error || apiError}</p>
            {apiError && (
              <p className="mt-1">Please try refreshing the page or contact support if the issue persists.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;